
<template>
  <div v-if="isOpen" class="modal-overlay" @click.self="close">
    <div class="modal-container" :class="{ 'modal-opening': isOpen }">
      <div class="modal-card">
        <!-- Header with Branding -->
        <div class="modal-brand-header">
          <div class="brand-logo">
            <i class="fas fa-hospital-user"></i>
          </div>
          <button type="button" class="close-btn" @click="close">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="modal-body p-4 pt-0">
          <div class="text-center mb-4">
            <h3 class="fw-bold text-dark mb-1">Welcome Back!</h3>
            <p class="text-muted small">Please enter your details to access your account</p>
          </div>

          <form @submit.prevent="handleSignIn" class="signin-form">
            <!-- Mobile Number Input -->
            <div class="form-group mb-3">
              <label class="custom-label">Mobile Number</label>
              <div class="input-wrapper" :class="{ 'wrapper-error': errors.mobile, 'wrapper-focus': activeField === 'mobile' }">
                <span class="input-icon"><i class="fas fa-phone-alt"></i></span>
                <input
                  v-model="form.mobile"
                  type="tel"
                  class="custom-input"
                  placeholder="01X-XXXX-XXXX"
                  :disabled="loading"
                  @focus="activeField = 'mobile'"
                  @blur="activeField = ''"
                  @input="errors.mobile = ''"
                />
              </div>
              <transition name="fade">
                <div v-if="errors.mobile" class="error-text"><i class="fas fa-exclamation-circle me-1"></i>{{ errors.mobile }}</div>
              </transition>
            </div>

            <!-- Password Input -->
            <div class="form-group mb-4">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <label class="custom-label mb-0">Password</label>
              </div>
              <div class="input-wrapper" :class="{ 'wrapper-error': errors.password, 'wrapper-focus': activeField === 'password' }">
                <span class="input-icon"><i class="fas fa-lock"></i></span>
                <input
                  v-model="form.password"
                  :type="showPassword ? 'text' : 'password'"
                  class="custom-input"
                  placeholder="••••••••"
                  :disabled="loading"
                  @focus="activeField = 'password'"
                  @blur="activeField = ''"
                  @input="errors.password = ''"
                />
                <button type="button" class="toggle-password" @click="showPassword = !showPassword">
                  <i class="fas" :class="showPassword ? 'fa-eye-slash' : 'fa-eye'"></i>
                </button>
              </div>
              <transition name="fade">
                <div v-if="errors.password" class="error-text"><i class="fas fa-exclamation-circle me-1"></i>{{ errors.password }}</div>
              </transition>
            </div>

            <!-- Sign In Button -->
            <button type="submit" class="submit-btn" :disabled="loading">
              <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
              <span v-else>Sign In to Account</span>
            </button>

            <!-- Footer Links -->
            <div class="modal-footer-custom mt-4">
              <p class="text-muted small mb-0">
                Don't have an account? 
                <a href="#" class="signup-link" @click.prevent="close">Create one now</a>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, inject } from 'vue';
import { useStore } from 'vuex';
import fetchUser from '@/services/modules/login';
import handleOnlineAppointment from '@/services/modules/online-appointment/onlineAppointment';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['close']);

const store = useStore();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const {
  setUserInLocalStorage,
  setTokenInLocalStorage,
  setPublicTokenInLocalStorage
} = fetchUser();

const {
  loginPatient
} = handleOnlineAppointment();

const loading = ref(false);
const showPassword = ref(false);
const activeField = ref('');
const errors = reactive({});
const form = reactive({
  mobile: '',
  password: ''
});

const validate = () => {
  Object.keys(errors).forEach(key => delete errors[key]);

  if (!form.mobile.trim()) {
    errors.mobile = 'Mobile number is required';
  } else if (!/^01[3-9]\d{8}$/.test(form.mobile.replace(/\s|-/g, ''))) {
    errors.mobile = 'Invalid mobile number format';
  }

  if (!form.password) {
    errors.password = 'Password is required';
  } else if (form.password.length < 6) {
    errors.password = 'Must be at least 6 characters';
  }

  return Object.keys(errors).length === 0;
};

const handleSignIn = async () => {
  if (!validate()) return;

  // implement login logic here
  loading.value = true;
  try {
    const res = await loginPatient({
      mobile: form.mobile.replace(/\s|-/g, ''),
      password: form.password
    });
    if (!res.status) {
      showError(res.message);
      return;
    }
    if (res.status) {
      setUserInLocalStorage(res.data);
      setTokenInLocalStorage(res.access_token);
      setPublicTokenInLocalStorage(res.public_access_token);
      store.commit('user/setUser');
      
      showSuccess('Login successful');
      close();
    }
  } catch (e) {
    showError('Something went wrong during login');
  } finally {
    loading.value = false;
  }
};

const close = () => {
  emit('close');
  form.mobile = '';
  form.password = '';
  Object.keys(errors).forEach(key => delete errors[key]);
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.modal-container {
  width: 100%;
  max-width: 420px;
  transform: translateY(20px);
  opacity: 0;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-opening {
  transform: translateY(0);
  opacity: 1;
}

.modal-card {
  background: #ffffff;
  border-radius: 24px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  position: relative;
}

.modal-brand-header {
  padding: 30px 30px 20px;
  display: flex;
  justify-content: center;
  position: relative;
}

.brand-logo {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #7367f0 0%, #9186f3 100%);
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 28px;
  box-shadow: 0 10px 20px -5px rgba(115, 103, 240, 0.4);
}

.close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  background: #f1f5f9;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #e2e8f0;
  color: #1e293b;
}

.custom-label {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 8px;
  display: block;
}

.input-wrapper {
  display: flex;
  align-items: center;
  background: #f8fafc;
  border: 2px solid #f1f5f9;
  border-radius: 12px;
  padding: 0 16px;
  transition: all 0.2s;
}

.wrapper-focus {
  background: #ffffff;
  border-color: #7367f0;
  box-shadow: 0 0 0 4px rgba(115, 103, 240, 0.1);
}

.wrapper-error {
  border-color: #ef4444;
  background: #fef2f2;
}

.input-icon {
  color: #94a3b8;
  font-size: 16px;
  margin-right: 12px;
}

.custom-input {
  border: none;
  background: transparent;
  width: 100%;
  padding: 12px 0;
  font-size: 15px;
  color: #1e293b;
  outline: none;
}

.custom-input::placeholder {
  color: #cbd5e1;
}

.toggle-password {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 8px;
  transition: color 0.2s;
}

.toggle-password:hover {
  color: #64748b;
}

.forgot-link {
  font-size: 12px;
  color: #7367f0;
  text-decoration: none;
  font-weight: 600;
}

.error-text {
  color: #ef4444;
  font-size: 12px;
  margin-top: 6px;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.submit-btn {
  width: 100%;
  background: linear-gradient(135deg, #7367f0 0%, #9186f3 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 14px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 12px rgba(115, 103, 240, 0.3);
  margin-top: 10px;
}

.submit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(115, 103, 240, 0.4);
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.modal-footer-custom {
  text-align: center;
}

.signup-link {
  color: #7367f0;
  text-decoration: none;
  font-weight: 700;
}

.signup-link:hover {
  text-decoration: underline;
}

/* Animations */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>

